<template>
  <v-app>
    <totp-notification />

    <new-version-system-toolbar />

    <notification-toolbar />

    <email-verification-system-toolbar />

    <account-delete-system-toolbar />

    <router-view />

    <app-notifications />

    <confirmation-dialog />

    <session-expired-modal />

    <!-- Loader overlay -->
    <v-fade-transition>
      <div v-show="!hasLoaded" class="loader-wrapper">
        <loader />
      </div>
    </v-fade-transition>
  </v-app>
</template>

<script lang="ts" setup>
import * as Sentry from '@sentry/vue';
import { useHead } from '@unhead/vue';
import { watchImmediate } from '@vueuse/core';
import { useFlags, useFlagsmithInstance } from 'flagsmith-vue';
import { storeToRefs } from 'pinia';
import { watch } from 'vue';
import { useTheme } from 'vuetify';

import { useMeStore } from '@abaninja/data-access-swiss21-users';
import { NewVersionSystemToolbar } from '@abaninja/feature-new-version';
import {
  AppNotifications,
  NotificationToolbar,
  TotpNotification,
  useProvideNotificationContext,
} from '@abaninja/feature-notification';
import { SessionExpiredModal } from '@abaninja/feature-session-expired';
import { useThemeColorMode } from '@abaninja/feature-theme';
import { provideAppNavigationIcon } from '@abaninja/ui-app-navigation';
import { ConfirmationDialog, provideConfirmationDialog } from '@abaninja/ui-dialog';
import { EmailVerificationSystemToolbar } from '@abaninja/ui-email-verification';
import { Loader, providePageLoader } from '@abaninja/ui-loader';
import { useRouterState } from '@abaninja/util-router';

import AccountDeleteSystemToolbar from './components/AccountDeleteSystemToolbar.vue';

const { hasLoaded } = storeToRefs(useRouterState());

// Set current theme
const theme = useTheme();
const themeColorMode = useThemeColorMode();

watch(themeColorMode.currentTheme, (value) => {
  theme.change(value);
}, { immediate: true });

// global providers
providePageLoader();
useProvideNotificationContext();
provideConfirmationDialog();

provideAppNavigationIcon();

useHead({
  titleTemplate: (title?: string) => !title ? 'Swiss21' : `${title} | Swiss21`,
});

const { entity } = storeToRefs(useMeStore());

// sentry
watchImmediate(entity, (value) => {
  if (!value) {
    return;
  }

  Sentry.setUser({
    id: value.uuid,
  });
});

const flagsmith = useFlagsmithInstance();

flagsmith.startListening(parseInt(import.meta.env.VITE_FLAGSMITH_POLLING_INTERVAL));

const { maintenance_window: maintenanceWindow } = useFlags(['maintenance_window']);

watch(maintenanceWindow, (value) => {
  if (value?.enabled) {
    // maintenance mode is active, redirect to maintenance window
    location.replace(new URL(import.meta.env.VITE_MAINTENANCE_WINDOW_PATH, import.meta.env.VITE_MAINTENANCE_WINDOW_URL).href);
  }
});
</script>

<style lang="scss">
@use './scss/settings';

:root{
  --border-radius-root: #{settings.$border-radius-root};
  --border-outline-opacity: #{settings.$field-outline-opacity};
  --spacer: #{settings.$spacer};
  --bootable-transition: #{settings.$bootable-transition};
  --grid-gutter: #{settings.$grid-gutter * 0.5};
  --field-control-padding: #{settings.$field-control-padding-top}
    #{settings.$field-control-padding-end}
    #{settings.$field-control-padding-bottom}
    #{settings.$field-control-padding-start};
  --field-control-padding-left: #{settings.$field-control-padding-start};
  --accelerated-easing: #{settings.$accelerated-easing};
}

.link a, a.link {
  color: rgb(var(--v-theme-primary)) !important;
  text-decoration: none !important;

  &:hover, &:focus {
    color: rgb(var(--v-theme-primary-darken-1)) !important;
  }
}
</style>

<style lang="postcss" scoped>
.loader-wrapper{
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(var(--v-theme-background));
  z-index: 2000;
}
</style>
